import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePrevious } from "react-use";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSnackbar } from "notistack";

import {
  apiSlice,
  useDeleteCustomWorkflowExecutionMutation,
  useExecuteCustomWorkflowMutation,
  useGetCustomWorkflowExecutionLogsQuery,
  useGetCustomWorkflowExecutionsQuery,
  useGetCustomWorkflowsQuery,
  useGetVersionQuery,
  WorkflowExecutionLog,
} from "fond/api";
import { useVersionScopedMutation } from "fond/hooks/useVersionScopedMutation";
import { refreshTileSource } from "fond/map/redux";
import { TaskStage, WorkflowKey } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { getTaskStages } from "fond/utils/taskStages";

export type LogsOutput = { type: "waiting" } | { type: "ready"; logs: WorkflowExecutionLog[] };

type Outputs = {
  isLoading: boolean;
  stages: TaskStage[] | null;
  workflowState: undefined | "IN_PROGRESS" | "COMPLETE" | "ERROR" | "PENDING" | "STARTING";
  workflowProgress: undefined | number;
  runWorkflow: null | ((parameters?: object | undefined) => Promise<void>);
  cancelWorkflow: null | (() => void);
  logs: LogsOutput | null;
};

type IProps = {
  // The ID of the version the workflow is associated with.
  versionId: string | undefined;

  // The key of the custom workflow to observe and/or execute.
  key: WorkflowKey | undefined;

  // Called when the workflow is observed moving from an in-progress state to the complete state.
  onComplete?: () => void;

  // Whether version data should be refetched when the workflow fails.
  refreshVersionDataOnFail?: boolean | undefined;
};

export function useCustomWorkflow({ versionId, key, onComplete, refreshVersionDataOnFail }: IProps): Outputs {
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const { refetch: refetchVersion } = useGetVersionQuery(versionId ?? skipToken);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { currentData: customWorkflowExecutions, isLoading: isLoadingWorkflows } = useGetCustomWorkflowExecutionsQuery(versionId ?? skipToken, {
    pollingInterval: pollingInterval !== null ? pollingInterval : 30000,
    skip: !key,
  });
  const { currentData: customWorkflows } = useGetCustomWorkflowsQuery(key ? versionId || skipToken : skipToken);
  // Don't share mutation state when the version changes
  const [executeCustomWorkflow, { isLoading, isSuccess, reset: resetExecuteMutation }] = useVersionScopedMutation(
    useExecuteCustomWorkflowMutation(),
    versionId
  );
  const [deleteCustomWorkflowExecution, { isLoading: isDeleting }] = useVersionScopedMutation(useDeleteCustomWorkflowExecutionMutation(), versionId);
  const workflowExecution = useMemo(() => {
    return customWorkflowExecutions && customWorkflowExecutions.CustomWorkflowExecutions.find((execution) => execution.CustomWorkflow.Key === key);
  }, [customWorkflowExecutions, key]);

  const waitingOnLogs = workflowExecution?.LogsStatus === "PENDING";
  // `currentData` here is required to ensure that we don't retain stale log data when the execution ID changes.
  const { currentData: logsData, isFetching: isFetchingLogs } = useGetCustomWorkflowExecutionLogsQuery(workflowExecution?.ID ?? skipToken, {
    skip: waitingOnLogs || workflowExecution?.Status.State !== "ERROR",
  });

  const logs = useMemo(() => {
    if (workflowExecution?.Status.State !== "ERROR") return null;
    if (waitingOnLogs || isFetchingLogs) return { type: "waiting" as const };
    return { type: "ready" as const, logs: logsData?.Logs ?? [] };
  }, [isFetchingLogs, logsData?.Logs, waitingOnLogs, workflowExecution?.Status.State]);

  const workflowState = useMemo(() => {
    if (isLoading || (isSuccess && !workflowExecution)) return "STARTING";
    return workflowExecution ? workflowExecution.Status.State : undefined;
  }, [isLoading, isSuccess, workflowExecution]);
  const stages = useMemo(() => getTaskStages(workflowExecution?.Status.Definition), [workflowExecution?.Status?.Definition]);
  const workflowProgress = workflowExecution && workflowExecution.Status.Progress !== null ? workflowExecution.Status.Progress : undefined;
  const previousWorkflowState = usePrevious(workflowState);
  const previousVersionId = usePrevious(versionId);

  // Get the custom workflow object
  const workflow = useMemo(() => {
    return customWorkflows && customWorkflows.CustomWorkflows.find((wf) => wf.Key === key);
  }, [customWorkflows, key]);

  // Run the custom workflow
  const runWorkflow = useMemo(() => {
    if (!workflow || !versionId) {
      return null;
    }

    return (parameters: object | undefined) => {
      return executeCustomWorkflow({ versionId, workflowId: workflow.ID, parameters })
        .unwrap()
        .catch(() => {
          enqueueSnackbar("Operation failed. Please try again...", { variant: "error" });
        });
    };
  }, [workflow, executeCustomWorkflow, versionId, enqueueSnackbar]);

  // Cancel the custom workflow by deleting it
  const cancelWorkflow = useMemo(() => {
    if (!workflowExecution || !versionId) {
      return null;
    }

    return () => {
      deleteCustomWorkflowExecution({ executionId: workflowExecution.ID, versionId })
        .unwrap()
        .then(() => resetExecuteMutation())
        .catch(() => {
          enqueueSnackbar("Failed to cancel the operation. Please try again...", { variant: "error" });
        });
    };
  }, [workflowExecution, versionId, deleteCustomWorkflowExecution, enqueueSnackbar, resetExecuteMutation]);

  const refreshVersionData = useCallback(() => {
    dispatch(refreshTileSource("project-layers"));
    dispatch(
      apiSlice.util.invalidateTags([
        { type: "FeatureTotals", id: versionId },
        { type: "Layers", id: versionId },
        { type: "VersionRootConfiguration", id: versionId },
        { type: "ImportStatus", id: versionId },
        { type: "BOM", id: versionId },
      ])
    );
    refetchVersion();
  }, [dispatch, refetchVersion, versionId]);

  const errorProcessingStatus = workflowExecution?.ErrorProcessingStatus;
  const isErrorProcessingInProgress = errorProcessingStatus === "IN_PROGRESS";

  // The version, and the ID of the execution whose processed errors we have already accounted for (or null if there is none).
  const errorsProcessed = useRef<{ versionId: string; executionId: string | null } | null>(null);

  useEffect(() => {
    if (!versionId || !customWorkflowExecutions) return;

    const processedExecutionId = errorProcessingStatus === "COMPLETE" && workflowExecution ? workflowExecution.ID : null;
    if (errorsProcessed.current?.versionId !== versionId) {
      // We never refresh version data on the first response for a version.
      //
      // There is a race condition here: the error processing task could finish in-between when the map
      // is loaded and when this component observes the `COMPLETE` state, causing us to miss the refresh. I believe
      // the "right" solution here is to include the last time any layer/feature in the version was changed in the
      // backend version response, however that's a much bigger change than I want to commit to right now.
      errorsProcessed.current = { versionId, executionId: processedExecutionId };
    } else if (processedExecutionId && processedExecutionId !== errorsProcessed.current.executionId) {
      // Error processing may have changed version data.
      errorsProcessed.current = { versionId, executionId: processedExecutionId };
      refreshVersionData();
    }
  }, [customWorkflowExecutions, errorProcessingStatus, refreshVersionData, versionId, workflowExecution]);

  useEffect(() => {
    // Trigger a refresh on the version if we observe the workflow move to a terminal state from elsewhere
    if (
      previousWorkflowState &&
      previousVersionId === versionId &&
      workflowState !== previousWorkflowState &&
      (workflowState === "COMPLETE" || (refreshVersionDataOnFail && workflowState === "ERROR"))
    ) {
      refreshVersionData();
      if (workflowState === "COMPLETE") {
        onComplete?.();
      }
    }

    // Alter the polling interval dynamically based on whether the workflow is currently executing
    if (workflowState === "IN_PROGRESS") {
      setPollingInterval(5000);
    } else if (workflowState === "ERROR" && (waitingOnLogs || isErrorProcessingInProgress)) {
      // Poll until we notice that logs are ready and errors have been processed
      setPollingInterval(5000);
    } else {
      setPollingInterval(null);
    }
  }, [
    isErrorProcessingInProgress,
    onComplete,
    previousVersionId,
    previousWorkflowState,
    refreshVersionData,
    refreshVersionDataOnFail,
    versionId,
    waitingOnLogs,
    workflowState,
  ]);

  return {
    workflowState,
    workflowProgress,
    stages,
    runWorkflow,
    cancelWorkflow,
    isLoading: isLoadingWorkflows || isLoading || isDeleting,
    logs,
  };
}
